/**
 * TrainingsTable
 *
 * Option A – Structured Compliance Tracker: dense table for trainings.
 * UnifyTable; parent slices pages. TrainingStatusBadge for status.
 */

import { Badge, Text } from '@mantine/core';
import { useMemo } from 'react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import type { Training } from '../types';
import { TrainingStatusBadge } from './TrainingStatusBadge';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

const COLUMNS: UnifyTableColumn<Training>[] = [
  {
    key: 'training',
    label: 'Training',
    render: (t) => (
      <>
        <Text fw={500} size="sm">
          {t.name}
        </Text>
        {t.enforcementNote && (
          <Text size="xs" c="dimmed" mt={2}>
            {t.enforcementNote}
          </Text>
        )}
      </>
    ),
    getValue: (t) => `${t.name} ${t.enforcementNote ?? ''}`,
  },
  {
    key: 'type',
    label: 'Type',
    render: (t) => (
      <Badge size="xs" variant="light" color={t.required ? 'orange' : 'gray'}>
        {t.required ? 'Required' : 'Optional'}
      </Badge>
    ),
    getValue: (t) => (t.required ? 'Required' : 'Optional'),
  },
  {
    key: 'status',
    label: 'Status',
    render: (t) => <TrainingStatusBadge status={t.status} />,
    getValue: (t) => t.status,
  },
  {
    key: 'completion',
    label: 'Completion',
    render: (t) => <Text size="sm">{formatDate(t.completionDate)}</Text>,
    getValue: (t) => formatDate(t.completionDate),
  },
  {
    key: 'expiration',
    label: 'Expiration',
    render: (t) => <Text size="sm">{formatDate(t.expirationDate)}</Text>,
    getValue: (t) => formatDate(t.expirationDate),
  },
  {
    key: 'verification',
    label: 'Verification',
    render: (t) =>
      t.verificationStatus ? (
        <Badge size="xs" variant="light" color={t.verificationStatus === 'verified' ? 'green' : 'gray'}>
          {t.verificationStatus === 'verified' ? 'Verified' : t.verificationStatus}
        </Badge>
      ) : (
        <Text size="sm" c="dimmed">
          —
        </Text>
      ),
    getValue: (t) => t.verificationStatus ?? '',
  },
];

interface TrainingsTableProps {
  trainings: Training[];
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
}

export function TrainingsTable({
  trainings,
  page = 1,
  pageSize = 10,
  onPageChange: _onPageChange,
}: TrainingsTableProps): JSX.Element {
  void _onPageChange;
  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return trainings.slice(start, start + pageSize);
  }, [trainings, page, pageSize]);

  return <UnifyTable withPaper={false} columns={COLUMNS} data={paginated} getRowKey={(t) => t.id} />;
}
